// src/components/XPModal.tsx
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { supabase } from '../utils/supabase';
import { XPHistoryEntry } from '../types/xp';
import { LevelIcon } from './LevelIcon';
import { useDarkMode } from '../context/DarkModeContext';
import { Link } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';

interface XPModalProps {
  isOpen: boolean;
  onClose: () => void;
  xpGained: number;
  level: number;
  totalXP: number;
  nextLevelXP: number;
}

export const XPModal: React.FC<XPModalProps> = ({
  isOpen,
  onClose,
  xpGained,
  level,
  totalXP,
  nextLevelXP,
}) => {
  const [progress, setProgress] = useState<any>(null);
  const [history, setHistory] = useState<XPHistoryEntry[]>([]);
  const { darkMode } = useDarkMode();
  const { user } = useAuthState();

  useEffect(() => {
    if (isOpen && user) {
      const fetchData = async () => {
        try {
          // Fetch progress using RPC directly
          const { data: progressData, error: progressError } = await supabase.rpc('get_progress', {
            p_user_id: user.id
          });
          
          if (!progressError && progressData) {
            setProgress(progressData);
          }

          // Fetch history - directly from the xp_history table
          const { data: historyData, error: historyError } = await supabase
            .from('xp_history')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(20);

          if (historyError) {
            console.error('Error fetching history:', historyError);
            return;
          }

          setHistory(historyData || []);
        } catch (error) {
          console.error('Error in fetchData:', error);
        }
      };

      fetchData();
    }
  }, [isOpen, user]);

  const formatActionType = (actionType: string, metadata: Record<string, any>) => {
    switch (actionType) {
      case 'note':
        return metadata.action === 'create' ? 'Created a new note' : 'Updated a note';
      case 'card':
        return metadata.action === 'create' ? 'Created a career card' : 'Updated a career card';
      case 'review':
        return 'Completed a review';
      case 'streak':
        return 'Daily streak bonus';
      default:
        return actionType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
  };

  const getActionDetails = (metadata: Record<string, any>) => {
    return '';
  };

  const renderStats = () => {
    if (!progress) return null;

    const { daily_xp, weekly_actions, monthly_actions } = progress.xp;

    return (
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className={`${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-900'} p-4 rounded`}>
          <h4 className="text-lg font-medium mb-2">Today's XP</h4>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Notes</span>
              <span>{daily_xp.note || 0} XP</span>
            </div>
            <div className="flex justify-between">
              <span>Cards</span>
              <span>{daily_xp.career_card || 0} XP</span>
            </div>
            <div className="flex justify-between">
              <span>Tags</span>
              <span>{daily_xp.tag || 0} XP</span>
            </div>
          </div>
        </div>

        <div className={`${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-900'} p-4 rounded`}>
          <h4 className="text-lg font-medium mb-2">This Week</h4>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Notes</span>
              <span>{weekly_actions.notes || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Cards</span>
              <span>{weekly_actions.career_cards || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Tags</span>
              <span>{weekly_actions.tags || 0}</span>
            </div>
          </div>
        </div>

        <div className={`${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-900'} p-4 rounded`}>
          <h4 className="text-lg font-medium mb-2">This Month</h4>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Reviews</span>
              <span>{monthly_actions.reviews || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Reflections</span>
              <span>{monthly_actions.reflections || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Resume Updates</span>
              <span>{monthly_actions.resume_updates || 0}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen || !progress) return null;

  return (
    <>
      {/* Transparent overlay for click-away functionality */}
      <div className="fixed inset-0 z-50" onClick={onClose} />
      
      {/* Modal content */}
      <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
        <div className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl pointer-events-auto`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">XP Progress</h2>
          <button 
            onClick={onClose} 
            className={`${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-500 hover:text-gray-700'}`}
          >
            ✕
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className={`${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-900'} p-4 rounded`}>
            <div className="text-lg font-medium">Level</div>
            <div className="flex items-center gap-2">
              <LevelIcon level={progress.xp.current_level} />
              <span className="capitalize">{progress.xp.current_level}</span>
            </div>
            <div className="text-sm mt-2">Total XP: {progress.xp.total_xp}</div>
          </div>
          
          <div className={`${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-900'} p-4 rounded`}>
            <div className="text-lg font-medium">Streak</div>
            <div className="flex flex-col">
              <div>Current: {progress.streak.current_streak} days</div>
              <div className="text-sm">Longest: {progress.streak.longest_streak} days</div>
            </div>
          </div>
        </div>

        {renderStats()}

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-4">Recent Activity</h3>
          <div className="space-y-2">
            {history.map((entry) => (
              <div key={entry.id} className={`flex justify-between items-center p-4 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-900'} rounded`}>
                <div className="grow">
                  <div className="font-medium">
                    {formatActionType(entry.action_type, entry.metadata)}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {format(new Date(entry.created_at), 'MMM d, yyyy h:mm a')}
                    </div>
                    {getActionDetails(entry.metadata) && (
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {getActionDetails(entry.metadata)}
                      </div>
                    )}
                  </div>
                </div>
                <div className={`flex items-center gap-2 ${darkMode ? 'text-green-400' : 'text-green-600'} font-medium ml-4`}>
                  <span className="text-lg">+{entry.xp_earned}</span>
                  <span className="text-sm">XP</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {progress.rewards?.some(r => r.eligible) && (
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Available Rewards</h3>
            <div className="space-y-2">
              {progress.rewards
                .filter(r => r.eligible)
                .map((reward) => (
                  <div key={reward.reward_type} className={`p-2 ${darkMode ? 'bg-green-900 text-white' : 'bg-green-50 text-gray-900'} rounded`}>
                    <div className="font-medium capitalize">
                      {reward.reward_type.replace('_', ' ')}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className="mt-4 text-center border-t pt-4">
          <Link 
            to="/gamification-support" 
            className="text-blue-600 hover:text-blue-800 text-sm"
            onClick={onClose}
          >
            Learn more about XP and Leveling
          </Link>
        </div>
      </div>
      </div>
    </>
  );
};